import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import type {
    ButtonColorAgainst,
    ButtonVariant
} from "@/libraries/design/components/button/src/button";
import { ContentBlock } from "@/libraries/design/components/content-block/src/content-block";
import { Image } from "@/libraries/design/components/image/src/image";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_MAX_WIDTH_XL,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_XL
} from "@/libraries/design/foundations/grid/src/base";

/**
 * Authorable props for the {@link UniformBleedingImageHero} pattern component.
 *
 * The pattern composes:
 * - An outer `<section>` that owns the section background and clips horizontal
 *   overflow caused by the bleeding image.
 * - A flexbox row containing two fixed-width children that match the GWP-11478
 *   Figma exactly:
 *     - {@link UniformContentBlock} on the left, with a fixed `width` per
 *       breakpoint so the heading and body wrap as designed.
 *     - {@link UniformImage} on the right with a fixed `width` per breakpoint
 *       and a negative right margin that lets the image bleed past the right
 *       edge of the viewport, while the image preserves its native aspect
 *       ratio rather than scaling down.
 * - On mobile (`< sm`) the layout stacks vertically and the image is rendered
 *   at full width so it is fully visible.
 */
export interface UniformBleedingImageHeroProps {
    /** Heading text rendered inside the {@link ContentBlock}. */
    heading: string;
    /** Body copy rendered inside the {@link ContentBlock}. */
    bodyText: string;
    /** Label for the call-to-action button. */
    ctaLabel: string;
    /** Authorable URL the call-to-action button navigates to. */
    ctaUrl: string;
    /** Source URL for the bleeding image rendered on the right. */
    mediaImageUrl: string;
    /** Accessible description for the bleeding image. */
    mediaAlt: string;
    /**
     * Optional background color for the outer section. Defaults to the dark
     * surface used in the WAC Refresh Teamwork Graph design (`#191919`).
     */
    backgroundColor?: string;
    buttonVariant?: ButtonVariant;
}

/**
 * Default outer-section background. Hard-coded to the GWP-11478 dark surface
 * (`#191919`).
 *
 * NOTE: an earlier iteration resolved this from
 * `wacToken("color.neutral.dark0")` (which yields `#18191A`, very close to
 * `#191919`). When rendered through Uniform's authoring runtime the resolved
 * value did not consistently propagate, so the consumer ended up with an
 * undefined background. Hard-coding the literal hex makes the default robust
 * to any token-resolution edge cases. Token migration can be revisited as a
 * follow-up if WAC introduces a `color.neutral.dark0`-equivalent that's safe
 * inside Uniform's runtime.
 */
// eslint-disable-next-line local/ensure-wac-token-usage
export const DEFAULT_BLEEDING_IMAGE_HERO_BACKGROUND_COLOR = "#191919";

/**
 * `colorAgainst` value passed to {@link Button} so it picks the white text +
 * white outline treatment intended for dark surfaces (`secondary_against_black`
 * in the underlying button style map).
 */
const BUTTON_COLOR_AGAINST_DARK_BG: ButtonColorAgainst = "black";

const sectionStyles = css({
    position: "relative",
    width: "100%",
    // `overflow-x: clip` prevents the bleeding image from triggering a
    // horizontal scrollbar at narrow viewports without creating a new scroll
    // container (unlike `overflow-x: hidden`).
    overflowX: "clip",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    // Mobile (< sm) padding per Figma frame 4846:58660 (the section is
    // padded on both sides so the stacked content has breathing room).
    paddingInline: token("space.400"),
    paddingBlock: token("space.500"),
    // From sm (768) up the section keeps the asymmetric padding from Figma:
    // generous left padding for the content block, generous right padding so
    // the image still has somewhere to bleed into.
    [media.above.sm]: {
        paddingInlineStart: WCL_GRID_PADDING_LG,
        paddingInlineEnd: WCL_GRID_PADDING_LG * 2,
        paddingBlock: 0
    },
    [media.above.md]: {
        paddingInlineStart: WCL_GRID_PADDING_LG,
        paddingInlineEnd: WCL_GRID_PADDING_LG * 2
    },
    [media.above.lg]: {
        paddingInlineStart: WCL_GRID_PADDING_XL,
        paddingInlineEnd: WCL_GRID_PADDING_XL * 2
    }
});

const rowStyles = css({
    maxWidth: WCL_GRID_MAX_WIDTH - WCL_GRID_PADDING_LG,
    [media.above.lg]: {
        maxWidth: WCL_GRID_MAX_WIDTH_XL - WCL_GRID_PADDING_XL
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    gap: token("space.500"),
    [media.above.sm]: {
        flexDirection: "row",
        gap: "0"
    }
});

const contentBlockSlotStyles = css({
    // Mobile: full width, transparent so the section background shows through.
    width: "100%",
    backgroundColor: "transparent",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: token("space.500"),
    alignItems: "flex-start",
    // The fixed widths below mirror the Figma frames for each breakpoint
    [media.above.sm]: {
        width: "312px",
        flexShrink: 0,
        gap: token("space.500"),
        paddingBlockStart: token("space.500"),
        paddingBlockEnd: token("space.500")
    },
    [media.above.md]: {
        width: "440px"
    },
    [media.above.lg]: {
        width: "468px",
        gap: token("space.800"),
        paddingBlockStart: token("space.800"),
        paddingBlockEnd: token("space.800")
    },
    // Sit visually above the image overlap region so the heading / CTA remain
    // interactive in the bleed zone.
    zIndex: 1
});

const imageSlotStyles = css({
    // Mobile: stack below the content, full width visible.
    width: "100%",
    backgroundColor: "transparent",
    overflow: "visible",
    flexShrink: 0,
    [media.above.sm]: {
        // Fixed widths from Figma frames per breakpoint. The image preserves
        // its native aspect ratio and uses a negative right margin to bleed
        // past the right edge of the viewport (the section's `overflow-x:
        // clip` swallows the overflow without adding horizontal scrollbars).
        width: "845px",
        marginInlineEnd: "-86px",
        marginInlineStart: "auto",
        paddingBlock: token("space.300")
    },
    [media.above.md]: {
        width: "845px",
        marginInlineEnd: "-126px"
    },
    [media.above.lg]: {
        width: "1163px",
        marginInlineEnd: "-168px"
    }
});

/**
 * Inline `<img>` styles for the bleeding image. Renders the image at the
 * width of its slot with proportional height so there is never any
 * letterboxing whitespace around it. `display: block` removes the inline
 * baseline gap that would otherwise add a few pixels under the image.
 */
const imageElementStyles = css({
    display: "block",
    width: "100%",
    height: "auto",
    maxWidth: "100%"
});

/**
 * `UniformBleedingImageHero` is a Uniform-registered pattern component that
 * composes a {@link ContentBlock} (left, heading + body only) and a
 * {@link Image} (right) inside a flexbox row, with a locally rendered
 * {@link Button} acting as the call-to-action. The image is rendered at a
 * fixed width per breakpoint and uses a negative right margin to bleed past
 * the right edge of the viewport (clipped by the section's `overflow-x:
 * clip`). On mobile the layout stacks and the image is shown in full.
 *
 * ----------------------------------------------------------------------------
 * Layout decision (Option X)
 * ----------------------------------------------------------------------------
 * Earlier iterations of this component used the WAC `ColumnLayout`'s 12-column
 * grid with the content block on cols 1–6 and the image on cols 5–12. After
 * comparing that against the GWP-11478 Figma frames, the design turns out to
 * be a flex layout of two **fixed-width** children (e.g. content block 312–
 * 668px, image 844–1438px) — not a fluid 12-col grid — which is why the
 * heading and body wrap as designed. Switching to a fixed-width flex row
 * matches the Figma exactly while still letting the image bleed past the
 * right edge thanks to the section's `overflow-x: clip`.
 *
 * ----------------------------------------------------------------------------
 * CTA architecture decision (recorded for future maintainers)
 * ----------------------------------------------------------------------------
 * `UniformContentBlock` *can* render its own button via `buttonLabel` /
 * `buttonHref` / `buttonVariant`, but its internal `renderCta` hardcodes
 * `colorAgainst` to `"white"` for every background except the WAC blue
 * (`#1868DB`), where it forces `"other"`. There is no code path that yields
 * `colorAgainst="black"` (the value required for white text + white outline
 * on a dark surface — see `getButtonVariant` in `button.tsx`, which maps it
 * to the `secondary_against_black` style).
 *
 * Two paths to fix the wrong-button-on-dark-bg:
 *   - Option A: patch ContentBlock so dark `backgroundColor` triggers
 *     `colorAgainst="black"`. Correct at the right layer; would also fix the
 *     latent bug for any future dark-bg ContentBlock consumers.
 *   - Option C (chosen here): keep ContentBlock for heading + body only and
 *     render the CTA `Button` ourselves with `colorAgainst="black"`.
 *
 * Option C was chosen for **this** PR for scope and risk reasons:
 *   1. It keeps every change inside `bleeding-image-hero/`, with zero blast
 *      radius on the shared `ContentBlock` (which is rendered in many places
 *      across WAC).
 *   2. ContentBlock currently has no other dark-bg consumers, so the latent
 *      bug isn't actively breaking anything in production today.
 *   3. The Option A patch is a clean, scoped follow-up that can ship in its
 *      own PR (with its own visual review of any newly dark-bg consumers)
 *      without blocking GWP-11478.
 *
 * If a second dark-bg consumer appears, prefer migrating both this component
 * and the new one to Option A so the dark-mode CTA logic lives in exactly
 * one place.
 */
const UniformBleedingImageHero = ({
    heading,
    bodyText,
    ctaLabel,
    ctaUrl,
    mediaImageUrl,
    mediaAlt,
    backgroundColor,
    buttonVariant = "secondary"
}: UniformBleedingImageHeroProps) => {
    // Resolve the background color defensively. Destructuring defaults only
    // apply when the prop is strictly `undefined`; Uniform's authoring runtime
    // can emit `null` or `""` for unset string parameters, both of which would
    // bypass a destructured default and leave the section unstyled. Treat any
    // falsy value as "use the default" so the dark surface is always painted.
    const resolvedBackgroundColor =
        backgroundColor && backgroundColor.trim() !== ""
            ? backgroundColor
            : DEFAULT_BLEEDING_IMAGE_HERO_BACKGROUND_COLOR;
    const sectionBackgroundStyle = {
        backgroundColor: resolvedBackgroundColor
    };

    return (
        <section
            css={sectionStyles}
            style={sectionBackgroundStyle}
            data-testid="bleeding-image-hero"
        >
            <div css={rowStyles}>
                <div css={contentBlockSlotStyles}>
                    {/*
                     * `backgroundColor` is forwarded the section's hex value
                     * so ContentBlock's luminance check (`isDarkColor`) trips
                     * and applies its `lightTextStyles` (white headings, body,
                     * eyebrows). ContentBlock has no `theme`/`textColor` prop;
                     * its only signal for dark-mode text is its OWN
                     * `backgroundColor`. Painting the same hex here is safe
                     * because the outer section already paints that color, so
                     * the visible result is unchanged but the text becomes
                     * white as designed in the GWP-11478 Figma.
                     */}
                    <ContentBlock
                        title={heading}
                        titleAs="h2"
                        text={bodyText}
                        backgroundColor={resolvedBackgroundColor}
                    />
                    {/*
                     * CTA rendered locally (not via ContentBlock's built-in
                     * button) so we can pass `colorAgainst="black"` for the
                     * dark-surface white-outline treatment. See the component
                     * doc comment above for the full Option-A vs Option-C
                     * rationale.
                     */}
                    <Button
                        variant={buttonVariant}
                        responsiveSize={{
                            mobile: "small",
                            tablet: "small",
                            desktop: "large",
                            xl_desktop: "large"
                        }}
                        colorAgainst={BUTTON_COLOR_AGAINST_DARK_BG}
                        href={ctaUrl}
                        aria-label={`${ctaLabel}, ${heading}`}
                        dataEventProps={{
                            "data-product-key": "uniform",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "heroSection",
                            "data-label": ctaLabel,
                            "data-label-english": ctaLabel
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
                <div css={imageSlotStyles}>
                    {/*
                     * Render via the design-system `Image` component (not
                     * `UniformImage` / `<Picture>`). The shared `<Picture>`'s
                     * orange-logic branch (see `image.tsx` lines 258–288)
                     * emits `<source>` elements with declared `width`/`height`
                     * attributes (480×270 mobile, 768×432 tablet). Combined
                     * with `object-fit: contain` this letterboxes the image
                     * inside a fixed aspect-ratio box on mobile (the empty
                     * space above/below the image that prompted this change),
                     * and the picked `<source>` is sticky on viewport resize
                     * so the image never grows back when going from a small
                     * to large breakpoint.
                     *
                     * The design-system `Image` component renders a single
                     * <img> for orange-logic URLs (with `width: 100%` baked
                     * in), sidestepping both bugs. `imageElementStyles`
                     * supplies the additional `height: auto; max-width: 100%;
                     * display: block;` so the slot's media-query-driven width
                     * cleanly drives the rendered image size on resize.
                     */}
                    <Image
                        src={mediaImageUrl}
                        alt={mediaAlt}
                        css={imageElementStyles}
                    />
                </div>
            </div>
        </section>
    );
};

export default UniformBleedingImageHero;
